import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fragment, useEffect, useState } from 'react'
import { addOutput, attachOutputFile, completeWorkItem, draftOutput, ledgerQuery, membersQuery, planQuery, promoteWorkItem, publishOutput, resolveWorkStep, reviseOutput, stakeholdersQuery, workItemsQuery }
  from '../api/jedi'
import { completeLabel } from '../shell/ResolveTray'
import { itemAssessment } from '../shell/itemAssessment'
import { WorkSurface } from '../shell/WorkSurface'
import { subjectText } from '../shell/subjectText'
import type { LedgerEntry, TodayEntry, WorkItemView } from '../api/types'
import {
  ASSERTS_BASIS,
  ASSERTS_LABEL,
  assertsLine,
  BANDS,
  byDueAscending,
  dueTone,
  entryFromRecord,
  entryFromTodayEntry,
  type WorkEntry,
  entryFromTask,
  entryFromWorkItem,
  isMyTask,
  daysSince,
  isUnassigned,
  outcomeBehind,
  itemsUnderTask,
  SORT_DEFAULT,
  sortRows,
  type SortDir,
  type SortKey,
  timelineGroups,
  CLEARED_LABEL,
  clearedLine,
  WorkColumnHeader,
  WorkEntryRow,
} from '../shell/WorkColumns'
import { deferToday, intakeQuestionsQuery, meQuery, outcomeWatchQuery, pendingSetsQuery, recordHistoryQuery, restoreToday, sendAsk, todayQuery }
  from '../api/jedi'
import type { MitigationInput, OriginInput } from '../shell/recordAssessment'
import { TimelineCard, TodayMovedRow, TodayTile } from '../shell/TodayTile'
import { ChipBar, STANDING_CHIPS, TODAY_LETTERS, TYPE_LETTERS, TYPE_WORDS } from '../shell/WorkFilters'
import { useEngagement } from '../lib/engagement'
import { usePersonZone } from '../lib/personTime'
import { LoadProblem } from '../shell/LoadProblem'
import { RecordSurface } from '../shell/RecordSurface'
import { useReassign } from '../shell/useReassign'
import { readRecordOf, readTaskOf } from '../shell/readRecord'
import { dayWords } from '../shell/dayWords'
import { recordSearch, useLinkedRecord } from '../shell/recordLink'
import { DUE_LABEL, type DueFilter, isDueFilter, matchesDue } from '../shell/engagementDue'
import { driftingOutcomes, todaySignals } from '../shell/workRank'

/*
  The lens tabs, all four of work-model.md §9, each built. The note that used to
  stand here said Timeline was deliberately absent because it was not built; it
  is, and a comment describing an older screen is how a file starts lying.

  THE DESIGN'S ORDER (ruled by Erik, with Jesse, 2026-09-22): Today, Mine, All,
  Timeline. `Work.dc.html` lists them that way, and the order is an argument:
  your day, then your work, then the engagement's, then what is coming. Timeline
  sat third here only because it arrived last.
*/
const LENSES = [
  ['today', 'Today'],
  ['mine', 'Mine'],
  ['all', 'All'],
  ['timeline', 'Timeline'],
] as const

const LENS_SUB: Record<'today' | 'mine' | 'timeline' | 'all', string> = {
  // Honest about which band this is. The design bands Today four ways, and
  // three of them need drift and staleness, which are not built. Saying so
  // beats rendering a rubric with three empty bands, which would read as a
  // quiet day rather than a missing capability.
  today: 'What J.E.D.I. would work today, banded by why. Drift, then dates, then what has gone quiet.',
  mine: 'Work assigned to you.',
  // The design's own hint, and it says what the lens is FOR rather than what it
  // contains: "what is coming, so a light today is not wasted".
  timeline: 'What is coming, so a light today is not wasted.',
  all: 'The whole engagement, whoever it belongs to.',
}

export const Route = createFileRoute('/_shell/work')({
  component: WorkAll,
  /*
    The metrics strip's landing (010). The filter arrives in the URL because
    condition 1 makes it a STATED filter rather than a hidden query: it has to
    survive a reload and a shared link, or the surface just looks mysteriously
    short.
  */
  // …and a cited record's door (wave 23): the chat's record chip lands here
  // for a C·R·A·I·D record or a work item.
  /*
    …and whose Today is being shown (the PM view, ruled 2026-09-22). It is in
    the URL for the same reason the due filter is: it is a STATED scope. It
    survives a reload, it can be sent to somebody, and — the reason it matters
    here — the shell can read it, so the chat answers about the same people the
    screen is showing without the two having to be wired together.
  */
  validateSearch: (search: Record<string, unknown>): {
    due?: DueFilter
    record?: string
    who?: string
    lens?: 'all'
  } => ({
    ...(isDueFilter(typeof search.due === 'string' ? search.due : null)
      ? { due: search.due as DueFilter }
      : {}),
    ...(typeof search.who === 'string' && search.who.length > 0 && search.who !== 'me'
      ? { who: search.who }
      : {}),
    ...recordSearch(search),
    // Home's "Open All on Work" arrives on All, the lens its rows were read
    // from. Only All can be asked for: the others are a person's own.
    ...(search.lens === 'all' ? { lens: 'all' as const } : {}),
  }),
})

/**
 * Work → All at wave-3 step-3 fidelity: the browse-everything view now shows
 * the worked C·R·A·I·D records alongside work items (ADR-0031 §8 — "Ledger"
 * stays internal vocabulary, never UI copy). A record row opens the shared
 * record panel — history plus the record door; READ-ONLY BY CONSTRUCTION
 * (C-033): the door is the only verb. The Today/Mine views, the T lens
 * (tasks in All), and the real WorkSurface grid arrive with their
 * workstreams; work-item rows keep origin (G-45) and attribution from day one.
 */
function WorkAll() {
  const { id: engagementId } = useEngagement()
  // The day every date on Work counts in: the person's (RAID #482).
  const zone = usePersonZone()
  const queryClient = useQueryClient()
  /*
    The four lenses are ratified (work-model.md §9). Three are reachable now;
    Timeline arrives with its own step and is deliberately not rendered as a
    tab that does nothing.

    Today reads the SAME query as Mine, not the account-wide one. The design is
    explicit and it is easy to get backwards: `Work.dc.html` bands over `MY`,
    which its own comment defines as "work items assigned to YOU". Today is a
    personal lens with a rubric on it, not an account view sorted by urgency.
    Building it over `all` would have put other people's overdue work in your
    day, and would have made a team-facing lens out of the one surface that is
    most obviously personal.
  */
  /*
    THE DEFAULT LENS IS TODAY, and this is read from the design rather than
    chosen: `Work.dc.html`'s component initialises with `lens: 'today'`.

    The badge question that usually rides with this does NOT arise yet.
    `nav-attention-counts` §4 binds the count and the landing view to one query,
    and §3a records Work's pairing as the `focus` count landing on the All lens
    filtered to those ids. Checked rather than assumed: NO Work badge exists in
    this app. The only rail badge is Home's pending-set count, and
    `queryKeys.navAttention` is a reserved key with no query behind it. So the
    binding condition has nothing to violate today, and changing the default
    lens cannot break it.

    When the eight §3a queries are built, the pairing they must honour is
    already written down and it is NOT "land on the default lens": §4 allows
    "its default lens OR a stated, removable filter", and the design implements
    the second - engaging the attention filter switches to All and shows a
    chip that offers to show every record again. Recorded here so that work
    starts from the answer rather than re-deriving it.
  */
  const { due, record, who, lens: arriving } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  /*
    010 §4 allows the landing to be "its default lens OR a stated, removable
    filter", and the design implements the second. So an arriving filter puts
    the surface on ALL - the account-scope lens the strip's charter names,
    person-agnostic and across all parties - rather than on Today, which is
    personal and would silently narrow the very count that was clicked.
  */
  // A linked record lands on All too: it is the one lens where every item
  // and every C·R·A·I·D record appears, so the cited one is on it.
  const [lens, setLens] = useState<'today' | 'mine' | 'timeline' | 'all'>(
    due || record || arriving === 'all' ? 'all' : 'today',
  )
  const clearDue = () => void navigate({ search: {} })
  /*
    THE SAME PREDICATE THE TILE COUNTED WITH (010 condition 2). Not a
    reimplementation of it: `matchesDue` is the function the topbar calls, over
    the same three react-query caches. 026 is the record of what happens when
    these are two derivations - the design's own strip under-reported Due soon
    by five, and its note says "there is no way for a reader to know which
    number is the real one."
  */
  const keep = (entry: WorkEntry) => (due ? matchesDue(entry, due, zone) : true)
  const me = useQuery(meQuery)
  const items = useQuery(workItemsQuery(engagementId, lens === 'all' ? 'all' : 'mine'))
  // Surveillance's read, for Today's drift bands. Its own query key, so a
  // plan change and a verdict change invalidate independently.
  const watch = useQuery(outcomeWatchQuery(engagementId))
  const [promoted, setPromoted] = useState<string | null>(null)
  // Which item's tray is open, and what J.E.D.I. said when it refused. The
  // tray is only ever opened BY a refusal - pressing Complete on an item with
  // nothing outstanding just completes it (022 §2).
  const [refusal, setRefusal] = useState<{ itemId: string; says: string } | null>(null)
  // Which item is open in the drill-down. One surface, never seven: WorkSurface
  // replaced every screen-local drawer, and a second one here would be the
  // eighth geometry it exists to remove.
  const [openItemId, setOpenItemId] = useState<string | null>(null)
  // A refused publish or revise, in the gate's own words.
  const [outputRefusal, setOutputRefusal] = useState<string | null>(null)

  const complete = useMutation({
    mutationFn: ({ workItemId, sendUp }: { workItemId: string; sendUp?: string[] }) =>
      completeWorkItem(engagementId, workItemId, sendUp),
    onSuccess: () => {
      setRefusal(null)
      void queryClient.invalidateQueries({ queryKey: ['eng', engagementId] })
    },
    onError: (error, { workItemId }) => {
      const said = completionRefusalOf(error)
      // A refusal we understand opens the way to finish. Anything else is
      // carried as it arrived rather than dressed up as a tray.
      if (said) setRefusal({ itemId: workItemId, says: said })
    },
  })

  /*
    REASSIGN, ON EVERY RECORD THAT HAS AN OWNER (Erik, 2026-09-22 and again on
    2026-09-23). Two doors, both drafting: the ledger's own for a C·R·A·I·D
    record, the drafting door with the record in scope for a plan record. The
    hook holds the choice so Plan can make the same offer without a second copy
    of it.
  */
  const reassign = useReassign(engagementId)

  const resolve = useMutation({
    mutationFn: ({ itemId, stepId, kind, reason }:
      { itemId: string; stepId: string; kind: string; reason?: string }) =>
      resolveWorkStep(engagementId, itemId, stepId, kind, reason),
    onSuccess: (item) => {
      // The tray closes itself the moment the count reaches zero. Nobody is
      // made to press something extra to be told they may finish.
      if (item.openSteps.length === 0) setRefusal(null)
      void queryClient.invalidateQueries({ queryKey: ['eng', engagementId] })
    },
  })

  /*
    Outputs (wave-15 step 4). None of these touch the completion path, and that
    is deliberate: C-036's refusal counts steps, sub-items and routed asks, and
    brief 17 rules that publishing does not block and review never locks a
    draft. An output changing state must never change whether Complete refuses.
  */
  const addAnOutput = useMutation({
    mutationFn: ({ itemId, name }: { itemId: string; name: string }) =>
      addOutput(engagementId, itemId, name),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['eng', engagementId] }),
  })

  /*
    The ask (ds-promotion 018's AskBar). Its refusals are sentences a person can
    act on - an ungoverned act names the three, and somebody outside the
    engagement is named as outside it - so they land in the same refusal slot the
    output verbs use rather than in a toast nobody reads.
  */
  const ask = useMutation({
    mutationFn: ({ itemId, upn, act }: { itemId: string; upn: string; act: string }) =>
      sendAsk(engagementId, itemId, upn, act),
    onSuccess: () => {
      setOutputRefusal(null)
      void queryClient.invalidateQueries({ queryKey: ['eng', engagementId] })
    },
    onError: (e: unknown) => setOutputRefusal(e instanceof Error ? e.message : String(e)),
  })

  const publish = useMutation({
    mutationFn: ({ itemId, outputId, deliverableId, audience }:
      { itemId: string; outputId: string; deliverableId: string; audience: string }) =>
      publishOutput(engagementId, itemId, outputId, deliverableId, audience),
    onSuccess: () => {
      setOutputRefusal(null)
      void queryClient.invalidateQueries({ queryKey: ['eng', engagementId] })
    },
    // The gate refuses in its own words. Carried as it arrived, never dressed
    // up: "an artifact with no deliverable is an orphan" is the sentence.
    onError: (error) => setOutputRefusal(completionRefusalOf(error)),
  })

  const revise = useMutation({
    mutationFn: ({ itemId, outputId }: { itemId: string; outputId: string }) =>
      reviseOutput(engagementId, itemId, outputId),
    onSuccess: () => {
      setOutputRefusal(null)
      void queryClient.invalidateQueries({ queryKey: ['eng', engagementId] })
    },
    onError: (error) => setOutputRefusal(completionRefusalOf(error)),
  })

  const draft = useMutation({
    mutationFn: ({ itemId, outputId, body }: { itemId: string; outputId: string; body: string }) =>
      draftOutput(engagementId, itemId, outputId, body),
    onSuccess: () => {
      setOutputRefusal(null)
      void queryClient.invalidateQueries({ queryKey: ['eng', engagementId] })
    },
    // Same channel as publish and revise: the API's sentence, carried as it
    // arrived. "…stays readable as it is. Start the next draft…" is the words.
    onError: (error) => setOutputRefusal(completionRefusalOf(error)),
  })

  const attach = useMutation({
    mutationFn: ({ itemId, outputId, file }: { itemId: string; outputId: string; file: File }) =>
      attachOutputFile(engagementId, itemId, outputId, file),
    onSuccess: () => {
      setOutputRefusal(null)
      void queryClient.invalidateQueries({ queryKey: ['eng', engagementId] })
    },
    // The door's own words: the accepted list, the size ceiling, or the
    // published refusal. Carried as they arrived - the server is the one that
    // decides, so it is the one that should be quoted.
    onError: (error) => setOutputRefusal(completionRefusalOf(error)),
  })

  /*
    Create task (G-44). "An action is not a task": a sourced action is filler
    work with no place in the plan, and promotion is the escape hatch for the
    one that turns out to carry weight. It is explicit and attributed, never
    automatic.

    It DRAFTS. The task arrives as a candidate to approve on Home, which is
    both C-033 and the routing law - so what this button honestly promises is
    a proposal, and the confirmation says so rather than claiming a task was
    made.

    PLACEMENT, worth flagging: G-44 says "Inputs offers no promotion
    affordance", because in the design sourced actions surface on Inputs. The
    built app surfaces work items on Work, and the routing law (B-1, ratified
    5 Aug, later than G-44) puts an act where its record lives. So it is here.
  */
  const promote = useMutation({
    mutationFn: (workItemId: string) => promoteWorkItem(engagementId, workItemId),
    onSuccess: () => {
      setPromoted('Proposed as a task. Approve it on Home to add it to the plan.')
      void queryClient.invalidateQueries({ queryKey: ['eng', engagementId] })
    },
  })

  /*
    "NOT TODAY", AND "PULL BACK IN" (ruled by Erik 2026-09-22). The day it comes
    back is the server's, in this person's zone, so nothing here sends one. It
    is a disposition on your own list: the record does not move, and nobody
    else's Today changes.
  */
  const notToday = useMutation({
    mutationFn: ({ key, reason }: { key: string; reason: string | null }) =>
      deferToday(engagementId, key, reason),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['eng', engagementId, 'today'] }),
  })
  const pullBack = useMutation({
    mutationFn: (key: string) => restoreToday(engagementId, key),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['eng', engagementId, 'today'] }),
  })
  const ledger = useQuery(ledgerQuery(engagementId))
  const plan = useQuery(planQuery(engagementId))
  const members = useQuery(membersQuery(engagementId))
  /*
    What is waiting on HOME, counted in Today's assert line (ruled by Erik
    2026-09-22: approvals and intake questions stay where they are, and Today
    says how much is there). The same two queries Home renders, so the count and
    the list cannot disagree.
  */
  const pendingSets = useQuery(pendingSetsQuery(engagementId))
  const intakeQuestions = useQuery(intakeQuestionsQuery(engagementId))

  /*
    Object id to a person, for the Assignee column. One map for the whole
    surface: every row asks the same question, so resolving per row would turn
    one column into N lookups over the same list.

    Two sources, in order of how much they actually know:

      1. The DIRECTORY, when the membership names a stakeholder. That is a real
         name, which is what the design's Assignee column carries throughout
         ("Marcus Lee", "Priya Sundaram"), and it is why EngagementMembership
         holds a StakeholderId at all.
      2. The UPN's local part, otherwise. Not a guess at a name, just the half
         of the address that identifies the person: "rowan.diaz" reads as
         somebody, "rowan.diaz@example.test" reads as a mailbox and is long
         enough to push the Due column off the row.

    The full address stays available as the cell's title, so nothing is hidden,
    only shortened.
  */
  const stakeholders = useQuery(stakeholdersQuery(engagementId))
  const stakeholderNames = new Map(
    (stakeholders.data ?? []).map((p) => [p.id, p.name] as const),
  )
  const nameByOid = new Map<string, string>()
  for (const m of members.data ?? []) {
    if (!m.objectId) continue
    const fromDirectory = m.stakeholderId ? stakeholderNames.get(m.stakeholderId) : null
    nameByOid.set(m.objectId, fromDirectory || m.upn.split('@')[0]!)
  }
  const nameOf = (oid: string) => nameByOid.get(oid) ?? null
  // A directory entry by its id: how a record's `owned_by` edge becomes the
  // name its Owner cell and its drawer show.
  const nameOfPerson = (id: string) => stakeholderNames.get(id) ?? null

  /*
    A UPN in the words a person reads, through the SAME directory the Assignee
    column uses. The fallback is the local part rather than the whole address:
    an ask that has to print an email to name somebody has already lost, but
    printing half of one is nearer a name than printing all of it.
  */
  const nameOfUpn = (upn: string) => {
    const m = (members.data ?? []).find((x) => x.upn === upn)
    const fromDirectory = m?.stakeholderId ? stakeholderNames.get(m.stakeholderId) : null
    return fromDirectory || upn.split('@')[0]!
  }
  const [selected, setSelected] = useState<
    { id: string; title: string; reference: string | null; family?: 'ledger' | 'plan' } | null
  >(null)

  const rows = items.data ?? []
  const openIndex = rows.findIndex((i) => i.id === openItemId)
  const open = openIndex >= 0 ? rows[openIndex]! : null

  /*
    Both kinds normalize into the one column anatomy (ds-promotion/011) and
    both default-sort by due ascending, which is the reference implementation's
    behaviour for exactly the reason the rule exists: two tables that sort
    differently do not read as one table at two scopes.

    The item rows keep their source item alongside, because the actions and the
    drill-down act on the work item while the row renders the normalized entry.
  */
  const myRole = me.data?.memberships?.find((m) => m.engagementId === engagementId)?.role
  const myOid = me.data?.objectId ?? null

  /*
    The T lens (009). Tasks come from the plan, which is the one owner of task
    structure - Work reads it flat and never writes it back.

    Mine shows MY tasks; the account scope shows every task, anyone's. Today
    draws the tasks the server's Today holds (ADR-0050), below, so this list is
    empty there: a task you own is on Today when it is due, overdue, never
    broken down, or waiting on your close.
  */
  const planNodes = plan.data ?? []
  const taskNodes = planNodes.filter((n) => n.nodeType === 'task')
  const taskRows =
    lens === 'today' || lens === 'timeline'
      ? []
      : taskNodes
          .map((node) => ({ node, under: itemsUnderTask(node.id, rows) }))
          .filter(({ node, under }) => (lens === 'all' ? true : isMyTask(node, under, myOid, [me.data?.name, me.data?.signInName])))
          .map(({ node, under }) => entryFromTask(node, under))
          .filter(keep)
          .sort(byDueAscending)

  /*
    THE SAME PREDICATE THE TILE COUNTED WITH (010 condition 2). Not a
    reimplementation of it: `matchesDue` is the function the topbar calls, over
    the same three react-query caches. 026 is the record of what happens when
    these are two derivations - the design's own strip under-reported Due soon
    by five, and its note says "there is no way for a reader to know which
    number is the real one."
  */
  /*
    A RECORD IS YOURS THROUGH YOUR DIRECTORY ENTRY (ADR-0050, ADR-0044 §2), which
    is the same rule the server's Today uses. It is read here rather than asked
    for: the membership list this surface already loads carries the link, and the
    records are already on the page for All.

    This replaces the note that used to stand here, that records "cannot answer
    mine". They can, since the rule exists; what they cannot answer is an
    ASSIGNEE, which is a different question.
  */
  const myStakeholderId = (members.data ?? [])
    .find((m) => m.objectId && m.objectId === myOid)?.stakeholderId ?? null
  const ownsRecord = (entry: LedgerEntry) =>
    myStakeholderId !== null
    && (entry.links ?? []).some((l) => l.linkType === 'owned_by' && l.toId === myStakeholderId)
  const recordRows = (ledger.data ?? [])
    .filter((entry) => lens !== 'mine' || ownsRecord(entry))
    .map((entry) => entryFromRecord(entry, nameOfPerson))
    .filter(keep).sort(byDueAscending)

  /*
    The two signals Today's rubric bands on, joined here because this is the one
    place that holds all three reads: the work items, the plan graph the walk
    needs, and surveillance's verdict on each outcome. Computed by `workRank.ts`
    (wave 23), the same module the parity check runs against the server's rank.
  */
  const now = new Date()
  const drifting = driftingOutcomes(watch.data ?? [])
  /*
    The outcome's NAME for the assessment's "what it blocks" clause. The walk
    already exists for the drift band; this only turns its answer back into the
    word a person reads, because a sentence that named an id would be the defect
    wave 16 spent a wave removing.
  */
  const outcomeNameOf = (subjectRef: string | null | undefined): string | null => {
    const id = outcomeBehind(subjectRef, planNodes)
    if (id === null) return null
    return planNodes.find((n) => n.id === id)?.name ?? null
  }

  const allItemRows = rows.map((item) => ({
    entry: entryFromWorkItem(item, subjectText(item), nameOf, todaySignals(item, planNodes, drifting, now)),
    item,
  }))
  const itemRows = allItemRows.filter(({ entry }) => keep(entry))
    .sort((a, b) => byDueAscending(a.entry, b.entry))

  /*
    ONE TABLE PER LENS (ruled by Erik, with Jesse, 2026-09-22, from the design).
    Mine and All draw a single table with a filter bar over it, where this
    surface stacked three tables under three headings: Records, Tasks, Work
    items. Erik on the design's Mine: "the 'Mine' view shows everything not just
    work items, and there's a nice filter at the top".

    Stacking by kind made the KIND the organising fact, and a person looking for
    what is due next had to read three lists and merge them. The filter makes
    kind a question instead, and one table can then sort by what the columns say
    (011), which is what makes two scopes read as one table.
  */
  const [typeFilter, setTypeFilter] = useState('all')
  const [standing, setStanding] = useState('open')
  /** Mine's third control: one task, and the work under it. All's: one person. */
  const [taskFilter, setTaskFilter] = useState('all')
  const [assigneeFilter, setAssigneeFilter] = useState('all')

  const lensRows: { entry: WorkEntry; item: WorkItemView | null }[] =
    lens === 'mine' || lens === 'all'
      ? [
          ...recordRows.map((entry) => ({ entry, item: null })),
          ...taskRows.map((entry) => ({ entry, item: null })),
          ...itemRows,
        ]
      : []
  /*
    SEARCH, over the one table (the design's, on both bars). It matches what the
    row SHOWS — its reference, its title, and the line under it — rather than
    anything the row keeps to itself, so a search that finds nothing is a fact
    about the list rather than about the query.
  */
  const [query, setQuery] = useState('')
  const matchesQuery = ({ entry }: { entry: WorkEntry }) => {
    const needle = query.trim().toLowerCase()
    if (needle === '') return true
    return [entry.reference, entry.title, entry.sub, entry.origin]
      .some((part) => part?.toLowerCase().includes(needle))
  }
  const inStanding = ({ entry }: { entry: WorkEntry }) =>
    standing === 'any' ? true : standing === 'closed' ? entry.closed : !entry.closed
  const underChosenTask = ({ entry, item }: { entry: WorkEntry; item: WorkItemView | null }) =>
    taskFilter === 'all'
    || entry.key === taskFilter
    || item?.subjectRef === `task:${taskFilter}`
  const forChosenPerson = ({ item }: { item: WorkItemView | null }) =>
    assigneeFilter === 'all'
    // A task and a record carry no assignee, so a question about one is not
    // about them: they leave rather than answering "no".
    || (assigneeFilter === 'none'
      ? item !== null && (item.assignees?.length ?? 0) === 0
      : item !== null && (item.assignees ?? []).includes(assigneeFilter))
  const scoped = lensRows
    .filter(inStanding)
    .filter(matchesQuery)
    .filter(lens === 'mine' ? underChosenTask : forChosenPerson)
  const lensCountOf = (letter: string) =>
    letter === 'all' ? scoped.length : scoped.filter((r) => r.entry.chip === letter).length
  // The table's own sort (the design's): due ascending until somebody asks for
  // something else. Clicking the sorted column flips it; clicking another jumps
  // to that column's own useful direction.
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'due', dir: 'asc' })
  const sortBy = (key: SortKey) =>
    setSort((now) => (now.key === key
      ? { key, dir: now.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: SORT_DEFAULT[key] }))
  const shownRows = sortRows(
    scoped.filter((r) => typeFilter === 'all' || r.entry.chip === typeFilter),
    sort.key, sort.dir)

  /*
    TODAY, SERVED (ADR-0050; ruled by Erik, with Jesse, 2026-09-22). Everything
    that needs you today, not just work items, banded and ordered by the server,
    which is also what the chat reads, so the two can never disagree. It was
    ranked here, over work items alone, so a task you own and set due today was
    on Mine and in the chat and missing from Today.

    Each entry is drawn as the row its kind already has on Work, with the reasons
    it is here in place of the row's second line.
  */
  const todayList = useQuery(todayQuery(engagementId, who))
  const everyone = useQuery(workItemsQuery(engagementId, 'all'))
  /*
    The served entry, joined to the record it points at. The reasons stay a
    LIST rather than being folded into the row's second line: the tile draws
    them in their own place, and the assert line above quotes the first one.
  */
  const todayRowOf = (t: TodayEntry) => {
    const band = Number(t.band) as 1 | 2 | 3 | 4
    const held = { key: t.key, band, why: t.why, reason: t.deferredReason ?? null, who: t.who ?? null }
    if (t.kind === 'work_item') {
      // Somebody else's item is not in the mine lens this surface loads, so the
      // PM view reads it from the engagement's own set, which Today already
      // holds for the work under a task.
      const item = allItemRows.find((r) => r.item.id === t.id)?.item
        ?? (everyone.data ?? []).find((i) => i.id === t.id)
      return [{
        ...held,
        entry: item
          ? entryFromWorkItem(item, subjectText(item), nameOf, todaySignals(item, planNodes, drifting, now))
          : entryFromTodayEntry(t),
        item: (item ?? null) as WorkItemView | null,
      }]
    }
    if (t.kind === 'task') {
      const node = planNodes.find((n) => n.id === t.id)
      return [{
        ...held,
        entry: node ? entryFromTask(node, itemsUnderTask(node.id, everyone.data ?? [])) : entryFromTodayEntry(t),
        item: null,
      }]
    }
    const record = (ledger.data ?? []).find((e) => e.id === t.id)
    return [{ ...held, entry: record ? entryFromRecord(record, nameOfPerson) : entryFromTodayEntry(t), item: null }]
  }
  const todayAll = (todayList.data?.entries ?? []).flatMap(todayRowOf)

  /*
    WHAT EACH LENS HOLDS, for the count beside its tab. Each is the lens's whole
    SCOPE rather than the rows a filter happens to leave, so the number means
    the same thing from every lens and does not move when a chip is pressed.
    What a filter left is said in the note line over the table, where the person
    can see what it was filtered from.

    Counted from the engagement's own set rather than from this lens's rows: the
    items query narrows to mine on three of the four lenses, and a count that
    changed with the lens it is read from would be exactly the defect 026
    records about two derivations of one number.
  */
  const everyItem = everyone.data ?? []
  const mineScope =
    everyItem.filter((i) => myOid !== null && (i.assignees ?? []).includes(myOid)).length
    + taskNodes.filter((n) => isMyTask(n, itemsUnderTask(n.id, everyItem), myOid,
      [me.data?.name, me.data?.signInName])).length
    + (ledger.data ?? []).filter(ownsRecord).length
  const allScope = everyItem.length + taskNodes.length + (ledger.data ?? []).length

  /*
    THE SELECTED ROW, RESOLVED BACK TO THE RECORD BEHIND IT, for the `read`
    body. The row carries a title and a reference, which is all a 340px panel
    ever needed; a drawer needs the record.

    Composed here for the same reason the work item's assessment is: this is
    the only place that holds the ledger, the plan and the directory at once,
    and an `owned_by` edge is only a name once the directory has been consulted.
    Doing it in the drawer would put a third read inside a renderer.

    The lookup tries the ledger first and the plan second rather than trusting
    `family`, because a selection can outlive a refetch, and a stale id that
    matches nothing renders nothing instead of a drawer titled after a record
    that is no longer there.
  */
  // The task a record's mitigation runs through, read as the T lens reads it,
  // so the drawer says the standing and the date its row says.
  const mitigationOf = (taskId: string): MitigationInput | null => {
    const node = taskNodes.find((n) => n.id === taskId)
    if (!node) return null
    const row = entryFromTask(node, itemsUnderTask(node.id, everyItem))
    return { name: node.name, description: node.description?.trim() || null, standing: row.standing, due: row.due }
  }
  // Where the record came from: the first line of its history, read through the
  // query the drawer's own history section reads, so it costs no second request.
  const history = useQuery({ ...recordHistoryQuery(engagementId, selected?.id ?? ''), enabled: !!selected })
  const birth = history.data?.[0]
  const origin: OriginInput | null = birth ? { at: birth.at, why: birth.why ?? null } : null
  const readRecord = (() => {
    if (!selected) return null
    const entry = (ledger.data ?? []).find((e) => e.id === selected.id)
    if (entry) return readRecordOf(entry, nameOfPerson, mitigationOf, zone, origin)
    const node = taskNodes.find((n) => n.id === selected.id)
    if (node) return readTaskOf(node, itemsUnderTask(node.id, everyItem), zone)
    return null
  })()

  const todayMoved = (todayList.data?.moved ?? []).flatMap(todayRowOf)

  /*
    THE TYPE FILTER (ruled by Erik, with Jesse, 2026-09-22). The design has no
    filter on Today, because its Today held work items alone. Ours holds every
    kind that can need you, so the bar Mine and All already draw is here too,
    over the letters Today can hold. D is not among them: a decision is made, so
    it is never on anybody's Today (ADR-0050).
  */
  const [todayType, setTodayType] = useState('all')
  const todayCountOf = (letter: string) =>
    letter === 'all' ? todayAll.length : todayAll.filter((r) => r.entry.chip === letter).length
  const todayRows = todayAll.filter((r) => todayType === 'all' || r.entry.chip === todayType)

  /*
    G-41's cleared day. `cleared` is NOT "Today is empty": a day nobody had
    work on and a day somebody finished must not read the same, and the reward
    is the only thing that entry is about.

    Held in session state on purpose. A resolved item leaves Today, so once it
    is gone the surface cannot tell from the data whether the list was cleared
    or was never populated. What it can know is that it HAD work a moment ago
    and does not now, which is exactly the moment worth marking.
  */
  const [hadTodayWork, setHadTodayWork] = useState(false)
  const [clearedCount, setClearedCount] = useState(0)
  // The whole list, never the filtered one: a day is not cleared because
  // somebody pressed R.
  const todayCount = todayAll.length
  useEffect(() => {
    if (todayCount > 0) {
      setHadTodayWork(true)
      // Remembered while the rows still exist. Once the day is cleared they are
      // gone, and a banner that cannot say how many is a banner worth less.
      setClearedCount(todayCount)
    }
  }, [todayCount])
  const clearedToday = lens === 'today' && hadTodayWork && todayCount === 0

  // G-28: unassigned work, counted where somebody is already looking.
  const unassignedCount = allItemRows.filter(({ entry }) => isUnassigned(entry)).length
  // Timeline is a date view of work. Tasks carry derived standing rather than
  // their own urgency, and records carry no date at all, so neither belongs on it.
  const groups = lens === 'timeline' ? timelineGroups(itemRows.map((r) => r.entry), zone) : []
  const rowFor = (key: string) => itemRows.find((r) => r.entry.key === key)!

  /*
    One set of verbs, whatever lens you are looking through. The column anatomy
    is identical across scopes by rule (011), and an item that offers different
    actions depending on the lens breaks the same promise from the other side:
    the row would stop being the same row.
  */
  const actionsFor = (item: WorkItemView) =>
    item.resolved ? null : (
      <>
        <button
          type="button"
          className="work-row-promote"
          onClick={() => promote.mutate(item.id)}
          disabled={promote.isPending}
        >
          Create task
        </button>
        {/*
          Never `disabled`, and it names its count rather than going dead
          (022 §2).

          G-22 puts one condition on it: an item that sits under a task and has
          produced something is completed by CHOOSING what goes up, and that
          choice lives on the item. So the row opens the item rather than
          completing blind. Two Completes with different semantics would be the
          "one act, not two" problem in a new place - and the ellipsis is the
          design's own convention for a verb that opens something, as on
          canvas 1f's "Publish...".
        */}
        <button
          type="button"
          className="work-row-complete"
          onClick={() =>
            item.sendsUpToTask && item.outputs.length > 0
              ? setOpenItemId(item.id)
              : complete.mutate({ workItemId: item.id })
          }
        >
          {item.sendsUpToTask && item.outputs.length > 0
            ? 'Complete...'
            : completeLabel(item.openSteps.length)}
        </button>
      </>
    )



  const select = (id: string, title: string, reference: string | null, family: 'ledger' | 'plan' = 'ledger') =>
    setSelected((current) => (current?.id === id ? null : { id, title, reference, family }))
  // The cited record, selected as a click on its row would select it: a
  // C·R·A·I·D record opens its panel, a work item its drawer.
  useLinkedRecord(record, (ledger.data ?? []).map((entry) => entryFromRecord(entry, nameOfPerson)), (entry) => entry.key,
    (entry) => setSelected({ id: entry.key, title: entry.title, reference: entry.reference ?? null }))
  useLinkedRecord(record, items.data, (item) => item.id, (item) => setOpenItemId(item.id))

  return (
    <section className="surface surface-wide">
      <header className="surface-head">
        <h1>Work</h1>
        <nav className="work-lenses" aria-label="Work lenses">
          {/*
            THE COUNT BESIDE EACH TAB (the design's). Each one counts what its
            own lens means, which is why they are not one number: Today counts
            what is still on the list, Mine counts everything of yours whatever
            the chips say, and All counts the rows the chips left. Timeline
            carries none: a count of "what is coming" would need a horizon
            nobody has stated.
          */}
          {LENSES.map(([key, label]) => {
            const count = key === 'today' ? todayAll.length
              : key === 'mine' ? mineScope
                : key === 'all' ? allScope
                  : null
            return (
              <button
                key={key}
                type="button"
                className={lens === key ? 'work-lens work-lens-on' : 'work-lens'}
                aria-current={lens === key ? 'page' : undefined}
                onClick={() => setLens(key)}
              >
                {label}
                {/* The space is load-bearing: without it the tab's accessible
                    name runs together as "Mine7". */}
                {count !== null && <span className="work-lens-count">{` ${count}`}</span>}
              </button>
            )
          })}
        </nav>
        <p className="surface-sub">{LENS_SUB[lens]}</p>
        {/*
          010 condition 1: the filter arrives VISIBLE AND REMOVABLE. A hidden
          query makes Work look mysteriously short and costs trust, which is
          why the chip states its own count and offers the way back rather than
          only marking that something is on. The count here is the rows on
          screen, so the chip and the list cannot disagree with each other any
          more than the chip and the tile can.
        */}
        {due && (
          <p className="work-due-chip">
            <span className="work-due-chip-label">
              {DUE_LABEL[due]} · {recordRows.length + taskRows.length + itemRows.length}
            </span>
            <button type="button" className="work-due-chip-clear" onClick={clearDue}>
              Show everything again
            </button>
          </p>
        )}
        {/* Session display (block I): who you are and what you are on THIS
            engagement. Mine is the lens that cannot mean anything without it. */}
        {/*
          G-28: the count of unassigned work, where somebody is already looking.
          The register's complaint is that Work "flags it orange but nothing
          routes it", so this is a link into the rows rather than a badge: it
          switches to the lens that contains them.
        */}
        {unassignedCount > 0 && lens !== 'today' && (
          <p className="work-unassigned">
            {unassignedCount === 1
              ? '1 work item has nobody on it.'
              : `${unassignedCount} work items have nobody on them.`}
            {lens !== 'all' && (
              <button type="button" className="work-unassigned-go" onClick={() => setLens('all')}>
                Show them
              </button>
            )}
          </p>
        )}
        {lens === 'mine' && me.data && (
          <p className="work-whoami">
            {me.data.name}
            {myRole ? `, ${myRole} on this engagement` : ', no role on this engagement'}
          </p>
        )}
      </header>

      {(items.isPending || ledger.isPending || (lens === 'today' && todayList.isPending)) && (
        <p className="surface-note">Loading…</p>
      )}
      <LoadProblem error={items.error ?? ledger.error ?? plan.error ?? (lens === 'today' ? todayList.error : null)} />

      <div className="work-layout">
        <div className="work-main">
          {/*
            TIMELINE IS CARDS, IN THE WEEK'S OWN COLUMNS (ruled by Erik, with
            Jesse, 2026-09-22). The design draws the week side by side —
            this week, next week, later — so the shape of what is coming is the
            thing you see first. It was four stacked seven-column tables here,
            which is the same information arranged as four more lists to read.

            The three dated columns stand even when empty ("Nothing due."),
            because an empty next week is information, and a column that
            vanishes changes the week's shape under the reader.
          */}
          {lens === 'timeline' && (
            <>
              <p className="surface-note timeline-guidance">
                Work items assigned to you, grouped by due date. An item uses its own due date,
                or its plan record’s expected end date when it has none.
              </p>
              <div className="timeline-columns">
                {groups.map((group) => (
                  <section key={group.label} className="timeline-column">
                    <div className={`timeline-head timeline-head-${group.tone}`}>
                      <span className="timeline-head-label">{group.label}</span>
                      <span className="timeline-head-count">
                        {group.entries.length === 1 ? '1 item' : `${group.entries.length} items`}
                      </span>
                      <span className="timeline-head-range">{group.range}</span>
                    </div>
                    {group.label === 'No date yet' && (
                      <p className="surface-note timeline-guidance">
                        Open an item and ask J.E.D.I. to set its due date. Review the proposal and choose Accept.
                      </p>
                    )}
                    {group.entries.length === 0 ? (
                      <p className="timeline-empty">Nothing due.</p>
                    ) : (
                      <ul className="timeline-list">
                        {group.entries.map((entry) => {
                          const row = rowFor(entry.key)
                          return (
                            <TimelineCard
                              key={entry.key}
                              entry={entry}
                              onToday={todayAll.some((t) => t.entry.key === entry.key)}
                              due={{
                                words: entry.due ? dayWords(entry.due) : 'No date',
                                tone: dueTone(entry, zone),
                              }}
                              onOpen={() =>
                                setOpenItemId((id) => (id === row.item.id ? null : row.item.id))
                              }
                            />
                          )
                        })}
                      </ul>
                    )}
                  </section>
                ))}
              </div>
            </>
          )}

          {/*
            THE FILTER BAR, over one table (ruled 2026-09-22). It replaces the
            three headings this surface used to stack — Records, Tasks, Work
            items — because the design puts the kinds in one list and the kind
            in a chip. The third control is the lens's own: Mine filters by the
            task above the work, All by the person doing it.
          */}
          {(lens === 'mine' || lens === 'all') && lensRows.length > 0 && (
            <div className="work-controls">
              <ChipBar
                label={lens === 'mine' ? 'Filter your work by kind' : 'Filter the engagement by kind'}
                value={typeFilter}
                onPick={setTypeFilter}
                chips={['all', ...TYPE_LETTERS].map((letter) => ({
                  value: letter,
                  label: letter === 'all' ? 'All' : letter,
                  words: TYPE_WORDS[lens][letter] ?? letter,
                  n: lensCountOf(letter),
                }))}
              />
              <ChipBar
                label="Show open records, closed ones, or both"
                value={standing}
                onPick={setStanding}
                chips={STANDING_CHIPS}
              />
              <input
                className="work-control-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label={lens === 'mine' ? 'Search your work' : 'Search the engagement'}
                placeholder={lens === 'mine'
                  ? `Search ${lensRows.length} of yours`
                  : `Search ${lensRows.length} records`}
              />
              {(typeFilter !== 'all' || standing !== 'open' || query !== ''
                || (lens === 'mine' ? taskFilter !== 'all' : assigneeFilter !== 'all')) && (
                <button
                  type="button"
                  className="work-control-clear"
                  onClick={() => {
                    setTypeFilter('all')
                    setStanding('open')
                    setQuery('')
                    setTaskFilter('all')
                    setAssigneeFilter('all')
                  }}
                >
                  Clear
                </button>
              )}
              {lens === 'mine' ? (
                <select
                  className="work-control-select"
                  aria-label="Filter by the task the work is under"
                  value={taskFilter}
                  onChange={(e) => setTaskFilter(e.target.value)}
                >
                  <option value="all">All tasks</option>
                  {taskRows.map((task) => (
                    <option key={task.key} value={task.key}>{task.title}</option>
                  ))}
                </select>
              ) : (
                <select
                  className="work-control-select"
                  aria-label="Filter by who is doing the work"
                  value={assigneeFilter}
                  onChange={(e) => setAssigneeFilter(e.target.value)}
                >
                  <option value="all">Everyone</option>
                  {(members.data ?? [])
                    .filter((m) => m.objectId)
                    .map((m) => (
                      <option key={m.objectId} value={m.objectId!}>
                        {nameOf(m.objectId!) ?? m.upn.split('@')[0]}
                      </option>
                    ))}
                  <option value="none">Unassigned</option>
                </select>
              )}
            </div>
          )}
          {promoted && <p className="surface-note">{promoted}</p>}
          {/*
            The cleared day (G-41). Distinct from an empty one, and deliberately
            louder: this is the reward the entry says the whole pattern exists
            for. "Bang it out and get the reward at the end."
          */}
          {clearedToday && (
            <div className="work-cleared">
              <span className="work-cleared-mark">{CLEARED_LABEL}</span>
              <span className="work-cleared-line">{clearedLine(clearedCount)}</span>
            </div>
          )}

          {/* `items.data`, not merely "not pending": a read that FAILED also
              finishes with no rows, and until 2026-09-17 it was told "Today is
              clear", which is the one sentence a failure must never produce. */}
          {/*
            WHAT TODAY SAYS ABOUT ITSELF, above the bands, and the filter over
            the kinds it holds. Both are the design's, one of them moved: the
            assert line is Today's own, and the filter bar is Mine's, brought
            here in the wave that gave Today every kind (ruled 2026-09-22).
          */}
          {lens === 'today' && todayList.data && !clearedToday && (
            <>
              <div className="today-asserts">
                <span className="today-asserts-label" title={ASSERTS_BASIS}>{ASSERTS_LABEL}</span>
                <span className="today-asserts-line">
                  {assertsLine(todayAll[0]?.entry ? {
                    reference: todayAll[0].entry.reference ?? null,
                    title: todayAll[0].entry.title,
                    why: todayAll[0].why,
                  } : null, todayAll.length, {
                    approvals: pendingSets.data?.length ?? 0,
                    questions: intakeQuestions.data?.length ?? 0,
                  })}
                </span>
              </div>
              {/*
                WHOSE TODAY (the PM view, ruled by Erik 2026-09-22): "what a PM
                could ask to see all things due today, not just their own". The
                design has no such control — its Today is one person's — so this
                is All's assignee dropdown, moved here and given You.

                It writes the scope into the URL, which is what lets the chat
                answer about the same people without the two being wired
                together.
              */}
              <div className="today-scope">
                <label className="today-scope-label" htmlFor="today-who">Whose Today</label>
                <select
                  id="today-who"
                  className="today-scope-select"
                  value={who ?? 'me'}
                  onChange={(e) => void navigate({
                    search: (old) => ({ ...old, who: e.target.value === 'me' ? undefined : e.target.value }),
                  })}
                >
                  <option value="me">You</option>
                  <option value="everyone">Everyone</option>
                  {(members.data ?? [])
                    .filter((m) => m.objectId && m.objectId !== me.data?.objectId)
                    .map((m) => (
                      <option key={m.objectId} value={m.objectId!}>
                        {nameOf(m.objectId!) ?? m.upn.split('@')[0]}
                      </option>
                    ))}
                </select>
                {who && (
                  <span className="today-scope-note">
                    {who === 'everyone'
                      ? 'Everyone\u2019s, except what has gone quiet: that stays personal.'
                      : 'Theirs, except what has gone quiet: that stays personal.'}
                  </span>
                )}
              </div>
              {todayAll.length > 0 && (
                <ChipBar
                  label="Filter Today by kind"
                  value={todayType}
                  onPick={setTodayType}
                  chips={['all', ...TODAY_LETTERS].map((letter) => ({
                    value: letter,
                    label: letter === 'all' ? 'All' : letter,
                    words: TYPE_WORDS.today[letter] ?? letter,
                    n: todayCountOf(letter),
                  }))}
                />
              )}
            </>
          )}

          {(lens === 'today' ? todayRows.length === 0 && todayAll.length > 0 && todayList.data : itemRows.length === 0 && items.data)
            && !clearedToday && (
            <p className="surface-note">
              {lens === 'today'
                ? // The filter is on and nothing of that kind needs you today.
                  `Nothing of that kind is on your Today. ${todayAll.length === 1 ? '1 item is' : `${todayAll.length} items are`} on it.`
                : lens === 'mine'
                  ? 'Nothing is assigned to you here.'
                  : lens === 'timeline'
                    ? <>
                        No assigned work items match this view.{' '}
                        <button type="button" className="timeline-guidance-action" onClick={() => setLens('all')}>
                          Find work in All
                        </button>{' '}
                        and ask J.E.D.I. to assign an item to you. For task and deliverable dates,{' '}
                        <Link className="timeline-guidance-action" to="/plan">open Plan</Link>.
                      </>
                    : // Cold start: Work fills from two places, and says which.
                      'No work yet. Work arrives from the plan\u2019s task breakdown and from what J.E.D.I. reads in your documents. Upload the SOW on Inputs and the first items come back as candidates to approve.'}
            </p>
          )}
          {lens === 'today' && !(clearedToday && todayRows.length === 0) && (
          <ul className="today-list">
            {todayRows.map(({ key, band, why, entry, item, who: rowWho }, index) => {
              /*
                The band heading is emitted before the first tile that falls in
                it, so the rubric is visible without a second pass over the data
                and without empty headings for bands nothing is in. A rubric that
                ranked silently would be a list a person has to trust rather than
                read. The band is the server's, as the order is.

                The count beside it is the design's, and it counts what is IN
                the band as drawn: with a filter on, a heading claiming the
                unfiltered number would be counting rows that are not there.
              */
              const previous = index > 0 ? todayRows[index - 1]!.band : 0
              const heading = band !== previous ? BANDS[band] : null
              const inBand = todayRows.filter((r) => r.band === band).length
              const open = () =>
                item
                  ? setOpenItemId((id) => (id === item.id ? null : item.id))
                  // A task or a record opens its record, where it lives.
                  : select(entry.key, entry.title, entry.reference ?? null,
                      entry.kind === 'task' ? 'plan' : 'ledger')
              return (
                <Fragment key={entry.key}>
                  {heading && (
                    <li className={`work-band work-band-${band}`}>
                      <span className={`today-band-dot today-band-dot-${band}`} />
                      <span className="work-band-label">{heading.label}</span>
                      <span className="today-band-count">{inBand === 1 ? '1 item' : `${inBand} items`}</span>
                      <span className="today-band-line" />
                      <span className="work-band-rule">{heading.rule}</span>
                    </li>
                  )}
                  <TodayTile
                    entry={entry}
                    band={band}
                    why={why}
                    onOpen={open}
                    /*
                      Mark done is a work item's Complete, "where one step
                      finishes it" (ruled). An item that sends its output up to
                      a task closes by CHOOSING what goes up (G-22), which is
                      the drawer's job, so the tile does not offer it.
                    */
                    onDone={item && !item.resolved && !(item.sendsUpToTask && item.outputs.length > 0)
                      ? () => complete.mutate({ workItemId: item.id })
                      : undefined}
                    doneLabel={item && item.openSteps.length > 0
                      ? `Mark done · ${item.openSteps.length} still open`
                      : 'Mark done'}
                    pending={complete.isPending || notToday.isPending}
                    who={rowWho}
                    /*
                      Your own list is the only one you can move things off:
                      "not today" is a note on your day, and somebody else's day
                      is not yours to arrange.
                    */
                    onNotToday={who ? undefined : (reason) => notToday.mutate({ key, reason })}
                  />
                </Fragment>
              )
            })}
          </ul>
          )}

          {/*
            MOVED OFF TODAY, held rather than dropped. The design's promise is
            the reason this section exists at all: "held with your reason -
            nothing dropped silently". Each comes back tomorrow on its own, and
            Pull back in brings it back now.
          */}
          {lens === 'today' && todayMoved.length > 0 && (
            <div className="today-moved">
              <div className="today-moved-head">
                <span className="today-moved-label">{`Moved off today · ${todayMoved.length}`}</span>
                <span className="today-moved-note">held with your reason, and back tomorrow</span>
              </div>
              <ul className="today-moved-list">
                {todayMoved.map(({ key, entry, reason, item }) => (
                  <TodayMovedRow
                    key={entry.key}
                    entry={entry}
                    reason={reason}
                    onOpen={() =>
                      item
                        ? setOpenItemId((id) => (id === item.id ? null : item.id))
                        : select(entry.key, entry.title, entry.reference ?? null,
                            entry.kind === 'task' ? 'plan' : 'ledger')}
                    onPullBack={() => pullBack.mutate(key)}
                  />
                ))}
              </ul>
            </div>
          )}
          {/*
            WHAT THE TABLE HOLDS, said above it (the design's note line). It
            counts what is SHOWN against what the lens holds, so a filter never
            makes the list quietly shorter, and it says the two things about
            this table that are not obvious: it holds work and records alike,
            and every column sorts.
          */}
          {(lens === 'mine' || lens === 'all') && lensRows.length > 0 && (
            <p className="work-note">
              {`Showing ${shownRows.length} of ${lensRows.length} `}
              {lens === 'mine' ? 'items that are yours, work and records alike' : 'records, whoever they belong to'}
              {' \u00b7 no rubric applied, sort any column'}
            </p>
          )}
          {lens !== 'timeline' && lens !== 'today' && !(clearedToday && lensRows.length === 0) && (
          <ul className="work-list">
            <WorkColumnHeader sort={sort} onSort={sortBy} />
            {shownRows.map(({ entry, item }) => (
              item ? (
                <WorkEntryRow
                  key={entry.key}
                  entry={entry}
                  // The anchor is Home's needs-work deep-link target (:target highlights it).
                  anchorId={`item-${item.id}`}
                  onOpen={() => setOpenItemId((id) => (id === item.id ? null : item.id))}
                >
                  {actionsFor(item)}
                </WorkEntryRow>
              ) : (
                /*
                  A task and a record OPEN, and neither ACTS (009 guardrail 2:
                  "Work never becomes a second place to enter task status"). The
                  panel they open is read-only with the record's door on it,
                  which is the same thing their tile on Today opens.
                */
                <WorkEntryRow
                  key={entry.key}
                  entry={entry}
                  selected={selected?.id === entry.key}
                  onOpen={() => select(entry.key, entry.title, entry.reference ?? null,
                    entry.kind === 'task' ? 'plan' : 'ledger')}
                />
              )
            ))}
          </ul>
          )}
        </div>

        {/*
          A C·R·A·I·D record and a task open the DRAWER now, in its `read` body
          (wave 24). They opened a 340px card beside this list until now, while
          a work item on the row above opened 1120px, and the design ruled
          against exactly that beside its own click handler a month before it
          was built: "Every record in All opens its work surface" (Jesse, 4 Aug).

          Composed here rather than in the drawer, as the work item's assessment
          is: this is the one place holding the directory, the plan and the
          ledger at once, which is what turns an `owned_by` edge into a name.
        */}
        {readRecord && (
          <RecordSurface
            record={readRecord}
            backLabel="Work"
            reassign={!readRecord.ownable ? undefined : {
              // The directory both doors resolve a name against.
              people: (stakeholders.data ?? []).map((p) => p.name),
              busy: reassign.busy,
              says: reassign.says,
              onReassign: (name) =>
                reassign.reassign(readRecord.scopeRef, readRecord.title, name),
            }}
            onClose={() => { setSelected(null); reassign.forget() }}
          />
        )}

        {open && (
          <WorkSurface
            item={open}
            position={{ i: openIndex + 1, n: rows.length }}
            refusal={refusal?.itemId === open.id ? refusal.says : null}
            /*
              Every input is a NAME or a NUMBER this route has already resolved -
              never a ref. The assessment module therefore cannot leak an
              identifier because it is never given one, which is the same
              safe-by-construction shape `sourceWords()` uses.

              This is also the one place that holds all three reads: the items,
              the plan graph the outcome walk needs, and surveillance's verdict.
              Computing it here rather than in the drawer is why the drawer stays
              a renderer.
            */
            assessment={itemAssessment({
              state: open.state,
              assignees: open.assignees.map((oid) => nameOf(oid)).filter((n): n is string => n !== null),
              due: open.effectiveDue ?? open.due ?? null,
              openSteps: open.openSteps.length,
              totalSteps: open.steps.length,
              outcome: outcomeNameOf(open.subjectRef),
              outcomeDrifting: (() => {
                const id = outcomeBehind(open.subjectRef, planNodes)
                return id !== null && drifting.has(id)
              })(),
              staleDays: daysSince(open.lastTouchedAt),
              sendsUpTo: open.sendsUpToTask ? open.subjectLabel ?? null : null,
              outputs: open.outputs.length,
            }, zone)}
            busy={
              resolve.isPending || complete.isPending
              || addAnOutput.isPending || publish.isPending || revise.isPending
            }
            onClose={() => setOpenItemId(null)}
            onPrev={openIndex > 0 ? () => setOpenItemId(rows[openIndex - 1]!.id) : undefined}
            onNext={
              openIndex < rows.length - 1
                ? () => setOpenItemId(rows[openIndex + 1]!.id)
                : undefined
            }
            onComplete={(sendUp) => complete.mutate({ workItemId: open.id, sendUp })}
            deliverables={(plan.data ?? []).filter((n) => n.nodeType === 'deliverable')}
            outputRefusal={outputRefusal}
            /*
              The engagement's own members, minus yourself: asking yourself to
              review your own work is a sentence that means nothing, and an
              option that produces one is a form with a wrong answer in it.
            */
            people={(members.data ?? [])
              .filter((m) => m.objectId !== me.data?.objectId)
              .map((m) => ({ upn: m.upn, name: nameOfUpn(m.upn) }))}
            nameOfUpn={nameOfUpn}
            me={me.data?.objectId ?? null}
            onAsk={(upn, act) => ask.mutate({ itemId: open.id, upn, act })}
            onAddOutput={(name) => addAnOutput.mutate({ itemId: open.id, name })}
            onPublishOutput={(outputId, deliverableId, audience) =>
              publish.mutate({ itemId: open.id, outputId, deliverableId, audience })
            }
            onReviseOutput={(outputId) => revise.mutate({ itemId: open.id, outputId })}
            onDraftOutput={(outputId, body) => draft.mutate({ itemId: open.id, outputId, body })}
            onAttachOutputFile={(outputId, file) => attach.mutate({ itemId: open.id, outputId, file })}
            onResolve={(stepId, kind, reason) =>
              resolve.mutate({ itemId: open.id, stepId, kind, reason })
            }
          />
        )}
      </div>
    </section>
  )
}

/**
 * The completion refusal, unwrapped from the transport line - the same shape
 * `ChangeSetReview` already uses for the guardrail block-and-ask, so a refusal
 * reads in J.E.D.I.'s own words rather than as a status code.
 */
function completionRefusalOf(error: unknown): string | null {
  const message = error instanceof Error ? error.message : String(error)
  const body = /^API 409 on \S+: (.+)$/s.exec(message)?.[1]
  if (!body) return null
  try {
    const parsed = JSON.parse(body) as { says?: string }
    return typeof parsed.says === 'string' ? parsed.says : null
  } catch {
    return null
  }
}
